import axios, { AxiosInstance, AxiosResponse, CancelTokenSource } from 'axios';
import qs from 'qs';
import { Store } from 'vuex';
import { ElMessageBoxOptions } from 'element-ui/types/message-box';
import _ from 'lodash';
import { RootStore } from '@/main/store/@types/store.d';
import { showNotification, ApiNotificationOption, showAlert, MessageBoxInfo, getMessageType, MessageTypeStatus, showConfirm } from '@/main/utils/notifications';
import LocaleCs from '@/main/localization/resource_cs.json';
import LocaleEn from '@/main/localization/resource_en.json';
import LocaleZh from '@/main/localization/resource_zh.json';

import { AUTH_LOGOUT, SET_CHANGE_PASSWORD, SET_RESET_PASSWORD,USER_ACTIVE_NEW_SESSION } from '../store/actionTypes';
import { TENANT_NAME } from '../store/authorization/constants';
import Vue from 'vue'
import VueCookies from 'vue-cookies'
import { sanitize } from '@/main/utils/html-sanitizer';

let store: Store<RootStore> | null = null;
let isPopupDisplayed = false;
const channel = new BroadcastChannel('sessionEnd');

const getLocalizationLanguage = (): 'cs' | 'zh' | 'en' => {
	let language = window.localStorage.getItem('i18nextLng');
	if (!language) {
		language = navigator.languages[0]?.split('-')[0] || 'en';
	}
	language = language === 'tw' ? 'zh' : language;
	if (language !== 'cs' && language !== 'zh' && language !== 'en') {
		language = 'en';
	}
	return language as 'cs' | 'zh' | 'en';
};

const getCurrentLanguageMap = (language: 'cs' | 'zh' | 'en') => ({
	cs: LocaleCs,
	en: LocaleEn,
	zh: LocaleZh,
})[language];

const localizeLocalSafe = (key: string, fallback: string): string => {
	const language = getLocalizationLanguage();
	try {
		const localized = _.get(getCurrentLanguageMap(language), key)?.Text;
		return localized || fallback;
	} catch {
		return fallback;
	}
};

Vue.use(VueCookies, { expires: '7d'})
// This is to avoid a dependency cycle with root store. If webpack supported cyclic
// dependencies, it could be replaced with `import { store } from '../store';`.
export function setStore(s: Store<RootStore>) {
	store = store || s;
}

const axiosInstance = axios.create({
	baseURL: `${process.env.VUE_APP_HTTP_DOMAIN}/dmsapi/api`,
	timeout: 120000,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
		
	},
	withCredentials: true,
});

export async function checkError(request: Promise<any>, showError = true): Promise<AxiosResponse<any> | any> {
	try {
		sessionStorage.setItem('showUnexpectedError', 'false');
		const response = await request;
		if(response.data == 'User login with new Session')
		{
			throw new Error('User login with new Session');
		}
		const UserMessagesList = response.data.UserMessageList;
		if (UserMessagesList?.length > 0) {
			const message = UserMessagesList.map((item: ApiNotificationOption) => item.Message).join('  ');
			const alert = {
				title: UserMessagesList[0].Title,
				message,
				options: {
					type: getMessageType(UserMessagesList[0].Type),
				} as ElMessageBoxOptions,
			};

			showAlert(alert);
		}
		return response;
	} catch (error) {
		sessionStorage.setItem('showUnexpectedError', 'false');
		if (error instanceof Error) {
			isPopupDisplayed = false;
			if (error.message == 'User login with new Session') {
				isPopupDisplayed = true;
				sessionStorage.setItem('newSessionDetected', 'true');
				store!.commit(USER_ACTIVE_NEW_SESSION, { message: 'UserActiveNewSession' });
                try { channel.postMessage({ type: 'sessionEnd', time: Date.now() }); } catch (e) { /* channel closed */ }
				return error.message;
			} else if (error.message.includes('Session is over. Please log in again')) {
				store!.commit(AUTH_LOGOUT);
                try { channel.postMessage({ type: 'sessionEnd', time: Date.now() }); } catch (e) { /* channel closed */ }
				sessionStorage.setItem('loggedIn', 'false');
				httpRequest.removeAxiosJwtQuery();
	 			httpRequest.removeAxiosTenantQuery();
				window.location.href = '/session-over';
				await showConfirm({
					message: localizeLocalSafe('SessionOverMessage', error.message),
					title: localizeLocalSafe('UserActiveSessionTitle', 'Notification'),
					options: {
						type: 'info',
						confirmButtonText: 'Ok',
						showClose:false,
						showCancelButton:false,
						closeOnClickModal:false,
					},
				});
				throw error;
			}
		  } 
		if (axios.isCancel(error)) throw error;
		if (!error.config) return error;
		if (error.response?.data === 'User login with new Session') {
			isPopupDisplayed = true;
			sessionStorage.setItem('newSessionDetected', 'true');
			store!.commit(USER_ACTIVE_NEW_SESSION, { message: 'UserActiveNewSession' });
            try { channel.postMessage({ type: 'sessionEnd', time: Date.now() }); } catch (e) { /* channel closed */ }
			return error.response?.data;
		} else if (error.response?.data?.indexOf('Session is over. Please log in again') > -1) {
			store!.commit(AUTH_LOGOUT);
            try { channel.postMessage({ type: 'sessionEnd', time: Date.now() }); } catch (e) { /* channel closed */ }
			sessionStorage.setItem('loggedIn', 'false');
			httpRequest.removeAxiosJwtQuery();
	 		httpRequest.removeAxiosTenantQuery();
			window.location.href = '/session-over';
			await showConfirm({
				message: localizeLocalSafe('SessionOverMessage', error.response?.data),
				title: localizeLocalSafe('UserActiveSessionTitle', 'Notification'),
				options: {
					type: 'info',
					confirmButtonText: 'Ok',
					showClose:false,
					showCancelButton:false,
					closeOnClickModal:false,
				},
			});
			throw error;
		} else if (error.response && error.response.status && (error.response.status === 401 || error.response.status === 403)) {
			//store!.commit(AUTH_LOGOUT);
			if (error.response?.data && error.response?.data?.includes('Request recognized unauthorised perform operation')) {
                return error.response?.data;
            }
			if (error.response?.data === 'Password Reset') store!.commit(SET_RESET_PASSWORD, true);
			if (error.response?.data === 'Password Expired' ||error.response?.data === 'Account Locked' ) 
			{
				return error.response?.data;
			}
			if (error.response.status === 401 && !isPopupDisplayed
				&& sessionStorage.getItem('loggedIn') === 'true' && window.location.pathname !== '/session-over') {
				// If user refreshed during "new session" dialog, skip unexpected error and go to session-over
				if (sessionStorage.getItem('newSessionDetected') === 'true') {
           		try { channel.postMessage({ type: 'sessionEnd', time: Date.now() }); } catch (e) { /* channel closed */ }
					sessionStorage.removeItem('newSessionDetected');
					sessionStorage.setItem('loggedIn', 'false');
					store!.commit(AUTH_LOGOUT);
					httpRequest.removeAxiosJwtQuery();
					httpRequest.removeAxiosTenantQuery();
					window.location.href = '/session-over';
					return;
				}
				const params = new URLSearchParams();
				const userId = store?.state.loggedUser.UzivatelData.PracovnikId;
				const userName = store?.state.loggedUser.UzivatelData.Login;
				const tenantName = window.localStorage.getItem(TENANT_NAME);
				const apiUrl = error.config?.url || 'unknown';
				if (!_.isNil(userId)) params.append('userId', String(userId));
				if (!_.isNil(userName)) params.append('userName', String(userName));
				if (!_.isNil(tenantName)) params.append('tenantName', String(tenantName));
				if (!_.isNil(apiUrl)) params.append('apiUrl', String(apiUrl));
				const logoutUrl = `${httpRequest.axiosInstance.defaults.baseURL}/Authentication/LogUnexpectedError?${params.toString()}`;

				fetch(logoutUrl, {
					method: 'POST',
					credentials: 'include',
				}).catch(e => {
					console.error('Issue arrived while logging unexpected error:', e);
				});

           		try { channel.postMessage({ type: 'sessionEnd', time: Date.now() }); } catch (e) { /* channel closed */ }
				sessionStorage.setItem('loggedIn', 'false');
				sessionStorage.setItem('showUnexpectedError', 'true');
				store!.commit(AUTH_LOGOUT); 
				httpRequest.removeAxiosJwtQuery();
				httpRequest.removeAxiosTenantQuery();
				isPopupDisplayed = true;
				const unexpectedHeader = localizeLocalSafe('UnexpectedErrorHeader', 'An unexpected error occurred.');
				const unexpectedBody = localizeLocalSafe('UnexpectedError', 'There was a problem communicating with our services.');
				const toContinue = localizeLocalSafe('ToContinue', 'Please log in again in a new browser window through GRP portal.');
				let finalMessage = sanitize(`${unexpectedHeader} ${unexpectedBody} ${toContinue}`);
				await showConfirm({
					message: finalMessage,
					title: localizeLocalSafe('UserActiveSessionTitle', 'Notification'),
					options: {
						type: 'info',
						confirmButtonText: 'Ok',
						showClose:false,
						showCancelButton:false,
						closeOnClickModal:false,
						callback: async (action: string) => {
							if (action === 'confirm') {
								window.location.href = '/session-over';
							}
						},
					},
				});
			}
		}
		// Network and HTTP/2 transport errors do not have a response. Preserve the
		// original error instead of throwing while trying to inspect response data.
		if (!error.response) {
			throw error;
		}
		if (showError) {
			let message = `${error.config.url} - ${error.message}`;
			let alert = {} as MessageBoxInfo;
			let type = MessageTypeStatus.INFO;
			let ApiNotification = [] as ApiNotificationOption[];
			const { response } = error;
			if (response.config.responseType === 'arraybuffer' || response.config.responseType === 'blob') {
				let blobBuff = ArrayBuffer;
				if (response.config.responseType === 'blob') {
					blobBuff = await response.data.arrayBuffer();
				}
				const enc = new TextDecoder('utf-8');
				const arrayBuffer = new Uint8Array(response.config.responseType === 'blob' ? blobBuff : response.data);
				ApiNotification = JSON.parse(enc.decode(arrayBuffer));
			} else if (!_.isNil(response.data)) {
				ApiNotification = response.data;
			}
			if (!_.isNil(ApiNotification) && !_.isNil(ApiNotification[0])) {
				const isArray = Array.isArray(ApiNotification);
				message = isArray ? ApiNotification.map((item) => item.Message).join('<br>') : (ApiNotification as any).Message;
				type = isArray ? ApiNotification[0].Type : (ApiNotification as any).Type;
				if (!_.isNil(message)) {
					alert = {
						title: isArray ? ApiNotification[0].Title : (ApiNotification as any).Title,
						message,
						options: {
							type: getMessageType(type),
						},
					};
				}
			}
			if (!_.isEmpty(alert) && type !== MessageTypeStatus.OTHER && error.response.status !== 500 && response.data) {
				showAlert(alert);
			} else if (error.response.status === 500) {
				showNotification({
					title: 'API Error',
					message,
					type: 'error',
				});
			}
		}
		let data = null;
		if (error.response.config.responseType === 'arraybuffer' || error.response.config.responseType === 'blob') {
			let blobBuff = ArrayBuffer;
			if (error.response.config.responseType === 'blob') {
				blobBuff = await error.response.data.arrayBuffer();
			}
			const enc = new TextDecoder('utf-8');
			const arrayBuffer = new Uint8Array(error.response.config.responseType === 'blob' ? blobBuff : error.response.data);
			data = JSON.parse(enc.decode(arrayBuffer));
		} else {
			// eslint-disable-next-line prefer-destructuring
			data = error.response.data;
		}
		if (_.isArray(data)) {
			return data.filter((m) => m.Type === MessageTypeStatus.OTHER);
		}
		if (data.Type === MessageTypeStatus.OTHER) {
			return data;
		}
		throw error;
	}
}

class HttpRequest {
	axiosInstance: AxiosInstance;

	constructor() {
		this.axiosInstance = axiosInstance;
		this.axiosInstance.interceptors.request.use(
			(config) => {
				// Update default params before each request
				config.params = {
				...config.params, // Keep existing params
				recentActivityDone: sessionStorage.getItem('recentActivityDone') === 'true',
				};
				return config;
			},
			(error) => {
				return Promise.reject(error);
			}
		);
		const tenantName = window.localStorage.getItem(TENANT_NAME);
		const jwtToken = window.localStorage.getItem('jwt');

		if (tenantName) {
			this.setAxiosTenantQuery(tenantName);
		}
		if (jwtToken) {
			this.setAxiosJwtQuery(jwtToken);
		}
	}

	readCsrfCookieAndSetHeader() {
		let csrfToken = null;
		if (document.cookie.indexOf('XSRF-TOKEN') > -1) {
			const v = document.cookie.match('(^|;) ?' + 'XSRF-TOKEN' + '=([^;]*)(;|$)');
			const r = v ? v[2] : null;
			csrfToken = r ?? Vue.$cookies.get('XSRF-TOKEN');
		} else if (Vue.$cookies.get('XSRF-TOKEN')) {
			csrfToken = Vue.$cookies.get('XSRF-TOKEN');
		}
		
		if (csrfToken) {
			this.axiosInstance.defaults.headers.common['X-XSRF-TOKEN'] = csrfToken;
		}
	}
	setAxiosTenantQuery(tenant: string) {
		window.localStorage.setItem(TENANT_NAME, tenant);
		this.axiosInstance.defaults.params = _.assign(
			this.axiosInstance.defaults.params, { tenant },
		);
	}

	setAxiosJwtQuery(jwt: string) {
		window.localStorage.setItem("jwt", jwt);
		this.axiosInstance.defaults.headers.Authorization = jwt; 
	}
	removeAxiosJwtQuery() {
		window.localStorage.removeItem("jwt");
		delete this.axiosInstance.defaults.headers.Authorization;
	}

	removeAxiosTenantQuery() {
		window.localStorage.removeItem(TENANT_NAME);
		this.axiosInstance.defaults.params = _.omit(
			this.axiosInstance.defaults.params, 'tenant',
		);
	}

	async fetchReport(
		reportName:string,
		data?: any,
		{
			showError = true,
			cancelPtr = null,
		} : { showError?: boolean, cancelPtr?: { value: CancelTokenSource | null } | null } = {},
	) {
		this.readCsrfCookieAndSetHeader();
		return this.fetchPdf(`/Reports/Reports/${reportName}`, data, { showError, cancelPtr });
	}


	async fetch<T = any>(
		methodName: string,
		data?: any,
		{
			showError = true,
			cancelPtr = null,
		} : { showError?: boolean, cancelPtr?: { value: CancelTokenSource | null } | null } = {},
		{
			errorAlert = ''
		}:{errorAlert?: string | number} = {},
	): Promise<AxiosResponse<T>> {
		if (errorAlert === 'undefined' || errorAlert === undefined || errorAlert === null)
		{
			const alert = {
				title: 'Error',
				message: 'Something went wrong or parameter not valid please contact system admin',
				options: {
					type: getMessageType(MessageTypeStatus.ERROR),
				} as ElMessageBoxOptions,
			};

			showAlert(alert);
			return null as any;
		}

		cancelPtr && !cancelPtr.value && (cancelPtr.value = axios.CancelToken.source());

		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.get(methodName, {
			params: data,
			paramsSerializer: (paramsData) => qs.stringify(paramsData, { arrayFormat: 'repeat', allowDots: true }),
			maxRedirects: 0,
			cancelToken: cancelPtr?.value?.token,
		}) 
		.then(response => {
			delete this.axiosInstance.defaults.headers.common["X-XSRF-TOKEN"];
			return response;
		}), showError);
	}

	fetchPdf(
		methodName: string,
		data?: any,
		{
			showError = true,
			cancelPtr = null,
		} : { showError?: boolean, cancelPtr?: { value: CancelTokenSource | null } | null } = {},
	) {
		cancelPtr && !cancelPtr.value && (cancelPtr.value = axios.CancelToken.source());
		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.get(methodName, {
			params: data,
			paramsSerializer: (paramsData) => qs.stringify(paramsData, { arrayFormat: 'repeat' }),
			responseType: 'arraybuffer',
			headers: {
				Accept: 'application/pdf',
			},
			cancelToken: cancelPtr?.value?.token,
		}), showError);
	}

	downloadXlsxReport(methodName:string, fileName: string){
		return this.axiosInstance.get(methodName,{responseType: 'arraybuffer',
		headers: {
			Accept: 'application/xlsx',
		}}).then(response => {
		  const link = document.createElement('a');
		  document.body.appendChild(link);
		  const blob: Blob = new Blob([response.data]);
		  const url = window.URL.createObjectURL(blob);
		  link.href = url;
		  var contentDisposition = response.headers['content-disposition'];
		  if(!this.isEmpty(contentDisposition))
		  {
			var file = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
			if(!this.isEmpty(file))
				fileName = file;
		  }
		  if(this.isEmpty(fileName))
		  	fileName="Report.xlsx";//Deafult report name.
		  link.download=fileName;
		  link.click();
		  window.URL.revokeObjectURL(url);
		  link.remove();
		  });
	  }

	   isEmpty(value:string) {
		return (value == null ||  value.trim().length === 0);
	  }

	fetchBlobs(
		methodName: string,
		data?: any,
		{
			showError = true,
			cancelPtr = null,
		} : { showError?: boolean, cancelPtr?: { value: CancelTokenSource | null } | null } = {},
	) {
		cancelPtr && !cancelPtr.value && (cancelPtr.value = axios.CancelToken.source());
		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.get(methodName, {
			params: data,
			responseType: 'blob',
			cancelToken: cancelPtr?.value?.token,
		}), showError);
	}

	postBlobs(methodName: string, data?: any, showError: boolean = true) {
		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.post(methodName, data, {
			responseType: 'blob',
		}), showError);
	}

	postPdf(methodName: string, data?: any, showError: boolean = true) {
		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.post(methodName, data, {
			responseType: 'arraybuffer',
			headers: {
				Accept: 'application/pdf',
			},
		}), showError);
	}

	uploadMultipart(methodName: string, formData: any, showError: boolean = true) {
		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.post(
			methodName,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			},
		), showError);
	}

	async post(
		methodName: string,
		data?: any,
		{
			showError = true,
			cancelPtr = null,
		} : { showError?: boolean, cancelPtr?: { value: CancelTokenSource | null } | null } = {},
	): Promise<AxiosResponse<any>> {
		cancelPtr && !cancelPtr.value && (cancelPtr.value = axios.CancelToken.source());

		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.post(methodName, data, {
			withCredentials: true,
			// params: data,
			maxRedirects: 0,
			cancelToken: cancelPtr?.value?.token,
			paramsSerializer: (paramsData:any) => qs.stringify(paramsData, { arrayFormat: 'repeat' }),
		}).then(response => {
			delete this.axiosInstance.defaults.headers.common["X-XSRF-TOKEN"];
			return response;
		}), showError);
	}

	postQuery(methodName: string, data?: any, showError: boolean = true) {
		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.post(methodName, null, {
			withCredentials: true,
			params: data,
			paramsSerializer: (paramsData) => qs.stringify(paramsData, { arrayFormat: 'repeat' }),
		}), showError);
	}

	update(methodName: string, data?: any, showError: boolean = true) {
		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.put(methodName, data), showError);
	}

	delete(methodName: string, id?: any, showError: boolean = true) {
		this.readCsrfCookieAndSetHeader();
		return checkError(this.axiosInstance.delete(methodName, { params: { id } }), showError);
	}
}

channel.onmessage = (e) => {
 if (e.data.type === 'sessionEnd'
    && sessionStorage.getItem('loggedIn') === 'true' && window.location.pathname !== '/session-over') {
	sessionStorage.setItem('loggedIn', 'false');
	isPopupDisplayed = true;
	window.location.href = '/session-over';
  }
};
export { HttpRequest };
export const httpRequest = new HttpRequest();
